import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Utensils, ChevronRight } from 'lucide-react';

import { getMyMealEntries, getMyMealLimits, getMyMealMenusByDate } from '../../services/myMealService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';

const Wrapper = styled.div``;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const CardHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const MealBlock = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const MealName = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px; font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex; align-items: center; gap: 8px;
`;

const MealItems = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px; color: #64748b;
  margin-top: 4px;
`;

const Macros = styled.div`
  display: flex; gap: 12px;
  margin-top: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; color: #A0A8B4;
`;

const ProgressRow = styled.div`
  margin-bottom: 14px;
`;

const ProgressLabel = styled.div`
  display: flex; justify-content: space-between;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px; color: #64748b;
  margin-bottom: 6px;
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, $pct)}%;
  background: ${({ $pct }) =>
    $pct >= 100 ? '#E53935'
    : $pct >= 75 ? '#f97316'
    : 'linear-gradient(90deg, #0291EF, #02D3BF)'};
  transition: width 0.25s ease;
`;

const Empty = styled.div`
  color: #A0A8B4;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  padding: 20px 0;
  text-align: center;
`;

const EntryRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const EntryMeta = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px; color: #64748b;
`;

const EntryMeal = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px; font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export default function MyMealsPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [todaysMenus, setTodaysMenus] = useState([]);
  const [entries, setEntries] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = todayIso();
    setLoading(true);
    Promise.all([
      getMyMealMenusByDate(today).then((r) => r.data?.data || []).catch(() => []),
      getMyMealEntries().then((r) => {
        const res = r.data?.data;
        return Array.isArray(res) ? res : (res?.data || []);
      }).catch(() => []),
      getMyMealLimits().then((r) => r.data?.data || null).catch(() => null),
    ]).then(([menus, ent, lim]) => {
      setTodaysMenus(menus);
      setEntries(ent);
      setLimits(lim);
    }).finally(() => setLoading(false));
  }, []);

  const todayEntries = useMemo(() => {
    const t0 = todayIso();
    return entries.filter((e) => e.granted && !e.cancelled && e.entryDate === t0);
  }, [entries]);

  const monthEntries = useMemo(() => {
    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
    return entries.filter((e) => e.granted && !e.cancelled && e.entryDate >= monthStart);
  }, [entries]);

  // Sunucu tarafındaki fallback ile aynı varsayılanlar (2 / 30).
  const dailyLimit = limits?.dailyLimit ?? 2;
  const monthlyLimit = limits?.monthlyLimit ?? 30;
  const dailyPct = Math.round((todayEntries.length / Math.max(1, dailyLimit)) * 100);
  const monthlyPct = Math.round((monthEntries.length / Math.max(1, monthlyLimit)) * 100);

  const handleWeekly = useCallback(() => {
    history.push('/my-meals/weekly');
  }, [history]);

  if (loading) return null;

  return (
    <Wrapper>
      <Breadcrumbs items={[{ label: t('canteen.myMeals') }]} />

      <Grid>
        <Card>
          <CardHeader>
            <CardTitle>{t('canteen.todaysMenu')}</CardTitle>
            <Button variant="ghost" onClick={handleWeekly}>
              {t('canteen.goToWeeklyMenu')} <ChevronRight size={14} />
            </Button>
          </CardHeader>
          {todaysMenus.length === 0 ? (
            <Empty>{t('canteen.noMenuForToday')}</Empty>
          ) : (
            todaysMenus.map((m) => (
              <MealBlock key={m.id}>
                <MealName>
                  <Utensils size={14} />
                  {m.mealName}
                </MealName>
                <MealItems>{(m.items || []).join(' • ')}</MealItems>
                {(m.calories || m.proteinGrams || m.fatGrams || m.carbsGrams) && (
                  <Macros>
                    {m.calories != null && <span>{m.calories} kcal</span>}
                    {m.proteinGrams != null && <span>P {m.proteinGrams}g</span>}
                    {m.fatGrams != null && <span>Y {m.fatGrams}g</span>}
                    {m.carbsGrams != null && <span>K {m.carbsGrams}g</span>}
                  </Macros>
                )}
              </MealBlock>
            ))
          )}
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('canteen.dailyProgress')} / {t('canteen.monthlyProgress')}</CardTitle></CardHeader>
          <ProgressRow>
            <ProgressLabel>
              <span>{t('canteen.dailyProgress')}</span>
              <span>{todayEntries.length} / {dailyLimit}</span>
            </ProgressLabel>
            <ProgressTrack><ProgressFill $pct={dailyPct} /></ProgressTrack>
          </ProgressRow>
          <ProgressRow>
            <ProgressLabel>
              <span>{t('canteen.monthlyProgress')}</span>
              <span>{monthEntries.length} / {monthlyLimit}</span>
            </ProgressLabel>
            <ProgressTrack><ProgressFill $pct={monthlyPct} /></ProgressTrack>
          </ProgressRow>
        </Card>
      </Grid>

      <Card style={{ marginTop: 16 }}>
        <CardHeader><CardTitle>{t('canteen.myMeals')}</CardTitle></CardHeader>
        {entries.length === 0 ? (
          <Empty>{t('canteen.noMealsYet')}</Empty>
        ) : (
          entries.map((e) => (
            <EntryRow key={e.id}>
              <div>
                <EntryMeal>{e.mealName}</EntryMeal>
                <EntryMeta>{new Date(e.entryTime).toLocaleString()}</EntryMeta>
              </div>
              <EntryMeta>
                {e.cancelled
                  ? t('canteen.cancelled')
                  : e.granted
                    ? t('canteen.granted')
                    : t(`responseErrors.${e.denyReason}`, { defaultValue: t('canteen.denied') })}
              </EntryMeta>
            </EntryRow>
          ))
        )}
      </Card>
    </Wrapper>
  );
}
